import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ExternalLink, Loader2, MessageSquareQuote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { setPageMeta } from '../lib/seo';
import { PageHeader } from '../components/Shared';
import { FACEBOOK_REVIEWS_URL } from '../lib/social';

const EMPTY = { display_name: '', review_text: '', source_url: '', consent: false, website: '' };

export default function LeaveReview() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const startedAt = useRef(Date.now());

  useEffect(() => {
    setPageMeta({
      title: 'Leave a Review | Steve Lesperance, Ward 8',
      description: 'Know Steve or have worked with him? Leave a review on the website, or read and add reviews on Facebook.',
      url: `${window.location.origin}/leave-a-review`,
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const textLen = form.review_text.trim().length;
  const canSubmit = form.display_name.trim().length >= 2 && textLen >= 20 && form.consent && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const r = await api.post('/reviews', { ...form, started_at: startedAt.current });
      setDone(r.data);
      toast.success('Review received. Thank you!');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Your review could not be sent. Please check the form and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        pageKey="leave-a-review"
        kicker="LEAVE A REVIEW"
        title="Share your experience with Steve"
        subtitle="Know Steve or have worked with him? Leave a review here on the website, or read what others are saying and add your own review on Facebook."
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="leave-review-page">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            {done ? (
              <Card className="border-emerald-200 bg-emerald-50" data-testid="leave-review-success">
                <CardContent className="p-6 sm:p-8">
                  <CheckCircle2 className="h-8 w-8 text-emerald-700" />
                  <p className="font-serif font-black text-2xl text-[#1a2744] mt-3" data-testid="leave-review-success-title">Thank you. Your review has been received.</p>
                  <p className="text-slate-700 mt-2 leading-relaxed" data-testid="leave-review-success-text">{done.message}</p>
                  {done.reference && <p className="text-xs text-slate-500 mt-2" data-testid="leave-review-reference">Reference: {done.reference}</p>}
                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press justify-center" data-testid="leave-review-success-read"><Link to="/reviews">Read published reviews <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
                    <Button asChild variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="leave-review-success-facebook"><a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer">Also review on Facebook <ExternalLink className="h-4 w-4 ml-1.5" /></a></Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="leave-review-form-card">
                <CardContent className="p-6 sm:p-8">
                  <p className="text-xs font-black uppercase tracking-widest text-[#8a6d2c]">WEBSITE REVIEW</p>
                  <h2 className="font-serif font-black text-2xl text-[#1a2744] mt-1">Leave a Website Review</h2>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid="leave-review-not-facebook-note">Your review is read by Steve's team and published on this website after approval. Submitting here does <strong>not</strong> post it to Facebook.</p>
                  <form onSubmit={submit} className="mt-6 space-y-5" data-testid="leave-review-form">
                    <div>
                      <Label htmlFor="review-name">Display name <span className="text-slate-400 font-normal">(shown with your review)</span></Label>
                      <Input id="review-name" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} maxLength={80} placeholder="For example: Mary K., Omemee" className="mt-1 h-12" required data-testid="leave-review-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="review-text">Your review</Label>
                      <Textarea id="review-text" value={form.review_text} onChange={(e) => set('review_text', e.target.value)} rows={6} maxLength={2000} placeholder="How do you know Steve? What was it like working with him?" className="mt-1" required data-testid="leave-review-text-input" />
                      <p className={`text-xs mt-1 ${textLen < 20 ? 'text-slate-500' : 'text-emerald-700'}`} data-testid="leave-review-text-count">{textLen < 20 ? `${20 - textLen} more characters needed` : `${textLen} / 2000`}</p>
                    </div>
                    <div>
                      <Label htmlFor="review-source">Link to where you originally posted this review <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input id="review-source" value={form.source_url} onChange={(e) => set('source_url', e.target.value)} maxLength={500} placeholder="https://www.facebook.com/... or another public page" className="mt-1 h-12" data-testid="leave-review-source-input" />
                    </div>
                    {/* Honeypot: hidden from people, filled by bots */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="review-website">Website</label>
                      <input id="review-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <Checkbox id="review-consent" checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" data-testid="leave-review-consent-checkbox" />
                      <Label htmlFor="review-consent" className="text-sm leading-relaxed font-normal text-slate-700 cursor-pointer">I agree that my display name and review may be published on stevelesperance.com. I understand it will be reviewed first and that it is not posted to Facebook.</Label>
                    </div>
                    {error && <p className="text-sm font-bold text-[#b91c1c]" data-testid="leave-review-error">{error}</p>}
                    <Button type="submit" disabled={!canSubmit} className="h-12 px-6 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press w-full sm:w-auto" data-testid="leave-review-submit-button">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquareQuote className="h-4 w-4 mr-2" />} Submit Review for Approval
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="lg:col-span-2">
            <Card className="bg-[#1a2744] border-0 text-white" data-testid="leave-review-facebook-card">
              <CardContent className="p-6 sm:p-8">
                <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]">FACEBOOK</p>
                <h2 className="font-serif font-black text-2xl mt-1">Read &amp; Leave Facebook Reviews</h2>
                <p className="text-slate-200 mt-2 leading-relaxed">Prefer Facebook? Read what others are saying and add your own review on the campaign page. Facebook reviews stay on Facebook; you can also paste a link to it in the form here.</p>
                <Button asChild className="mt-5 h-12 w-full bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press justify-center" data-testid="leave-review-facebook-button">
                  <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer">Read &amp; Leave Facebook Reviews <ExternalLink className="h-4 w-4 ml-1.5" /></a>
                </Button>
              </CardContent>
            </Card>
            <Card className="mt-4" data-testid="leave-review-privacy-card">
              <CardContent className="p-5">
                <p className="font-serif font-bold text-[#1a2744]">What is published</p>
                <ul className="text-sm text-slate-600 mt-2 space-y-1.5 list-disc pl-5">
                  <li>Your display name and review text, once approved.</li>
                  <li>The optional link to your original post, if you add one.</li>
                  <li>Nothing else. No email address or phone number is collected on this form.</li>
                </ul>
                <Link to="/reviews" className="inline-flex items-center gap-1 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2 mt-3" data-testid="leave-review-read-link">Read published reviews <ArrowRight className="h-4 w-4" /></Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
